import { useTranslation } from "react-i18next";
import { ArrowUpDown, Plus, RotateCcw, X } from "lucide-react";
import type { CampaignListSortField, CampaignListSortRule } from "@multi-chat/shared";
import { CAMPAIGN_LIST_SORT_FIELDS, uniqueCampaignListSortRules } from "@multi-chat/shared";
import { Button } from "../../components/ui/button/index.js";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover/index.js";

const FIELD_LABEL: Record<CampaignListSortField, string> = {
  name: "campaigns.list.columns.name",
  status: "campaigns.list.columns.status",
  updatedAt: "campaigns.list.columns.updated",
  scheduledAt: "campaigns.list.columns.scheduled",
};

function unusedSortField(rules: readonly CampaignListSortRule[]): CampaignListSortField | null {
  const used = new Set(rules.map((rule) => rule.field));
  return CAMPAIGN_LIST_SORT_FIELDS.find((field) => !used.has(field)) ?? null;
}

function defaultDirection(field: CampaignListSortField): CampaignListSortRule["direction"] {
  return field === "name" || field === "status" ? "asc" : "desc";
}

export function CampaignsSortPanel({
  sort,
  onChange,
}: {
  sort: readonly CampaignListSortRule[];
  onChange: (next: CampaignListSortRule[]) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const nextField = unusedSortField(sort);

  return (
    <div className="grid gap-2" data-testid="campaign-list-sort-popover">
      {sort.length === 0 ? (
        <p className="m-0 px-1 py-1 text-[0.75rem] text-muted">{t("campaigns.list.sortEmpty")}</p>
      ) : (
        sort.map((rule, index) => (
          <div
            key={`${rule.field}-${index}`}
            className="grid grid-cols-[minmax(0,1fr)_minmax(0,7rem)_auto] items-center gap-1"
            data-testid="campaign-list-sort-rule"
          >
            <select
              className="h-8 min-w-0 rounded-md border border-line/70 bg-surface px-2 text-[0.75rem]"
              value={rule.field}
              aria-label={t("campaigns.list.sortField")}
              onChange={(event) => {
                const field = event.target.value as CampaignListSortField;
                const next = sort.map((item, itemIndex) =>
                  itemIndex === index ? { ...item, field } : item,
                );
                onChange(uniqueCampaignListSortRules(next));
              }}
            >
              {CAMPAIGN_LIST_SORT_FIELDS.map((field) => (
                <option
                  key={field}
                  value={field}
                  disabled={sort.some((item, itemIndex) => item.field === field && itemIndex !== index)}
                >
                  {t(FIELD_LABEL[field])}
                </option>
              ))}
            </select>
            <select
              className="h-8 rounded-md border border-line/70 bg-surface px-2 text-[0.75rem]"
              value={rule.direction}
              aria-label={t("campaigns.list.sortDirection")}
              onChange={(event) => {
                const direction = event.target.value as CampaignListSortRule["direction"];
                onChange(
                  sort.map((item, itemIndex) =>
                    itemIndex === index ? { ...item, direction } : item,
                  ),
                );
              }}
            >
              <option value="asc">{t("campaigns.list.sortAsc")}</option>
              <option value="desc">{t("campaigns.list.sortDesc")}</option>
            </select>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="size-8 p-0"
              aria-label={t("campaigns.list.removeSort")}
              onClick={() => onChange(sort.filter((_, itemIndex) => itemIndex !== index))}
            >
              <X size={14} aria-hidden="true" />
            </Button>
          </div>
        ))
      )}
      <div className="flex flex-wrap gap-1 pt-1">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8"
          disabled={nextField == null}
          onClick={() => {
            if (nextField == null) return;
            onChange([...sort, { field: nextField, direction: defaultDirection(nextField) }]);
          }}
          data-testid="campaign-list-add-sort"
        >
          <Plus size={14} aria-hidden="true" />
          {t("campaigns.list.addSort")}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8"
          disabled={sort.length === 0}
          onClick={() => onChange([])}
          data-testid="campaign-list-reset-sort"
        >
          <RotateCcw size={14} aria-hidden="true" />
          {t("campaigns.list.resetSort")}
        </Button>
      </div>
    </div>
  );
}

export function CampaignsSortPopover({
  sort,
  onChange,
}: {
  sort: readonly CampaignListSortRule[];
  onChange: (next: CampaignListSortRule[]) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const count = sort.length;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-9 shrink-0"
          data-testid="campaign-list-sort"
          aria-label={t("campaigns.list.sort")}
        >
          <ArrowUpDown size={14} aria-hidden="true" />
          {count > 0 ? t("campaigns.list.sortCount", { count }) : t("campaigns.list.sort")}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[min(22rem,calc(100vw-2rem))] p-2">
        <CampaignsSortPanel sort={sort} onChange={onChange} />
      </PopoverContent>
    </Popover>
  );
}
